/**
 * @fileoverview State + actions for the Teacher Final Assessment Drawer (AR-579).
 *
 * Mirrors `useTeacherAssessmentDrawer` (lesson) but project-scoped and trimmed:
 *   - fetch lifecycle (composer service: list + per-student details)
 *   - student selection (multi + "All students")
 *   - optimistic individual score updates with rollback + toast
 *   - bulk apply by `lv4ItemId` with an overwrite confirmation prompt
 *
 * The final assessment has no deliverables and no enable/disable toggles
 * (those are lesson-only in the prototype), so this hook is a strict subset.
 * `t` is captured via a ref to avoid the unstable-`t` dep loop
 * (see feedback_useArcTranslation_deps).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconX } from '@tabler/icons-react'
import { notifications } from 'context'
import { ALL_STUDENTS_VALUE } from '../teacherAssessmentDrawer/constants'
import { fetchFinalAssessmentDrawerData, finalScoreUpdater } from './service'
import type { FinalAssessmentDrawerData, FinalAssessmentItem, FinalDrawerStudent } from './types'

export interface OverwritePrompt {
  itemKey: string
  masterScore: number
  affectedStudentIds: string[]
}

interface UseFinalAssessmentDrawerParams {
  opened: boolean
  projectId: string
  projectName: string
  initialStudentId?: string | null
}

export function useFinalAssessmentDrawer({
  opened,
  projectId,
  projectName,
  initialStudentId,
}: UseFinalAssessmentDrawerParams) {
  const { t } = useTranslation()
  const tRef = useRef(t)
  tRef.current = t

  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<FinalAssessmentDrawerData | null>(null)
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([ALL_STUDENTS_VALUE])
  const [overwritePrompt, setOverwritePrompt] = useState<OverwritePrompt | null>(null)

  const updater = useMemo(() => finalScoreUpdater(projectId), [projectId])

  // Load on open.
  useEffect(() => {
    if (!opened || !projectId) return
    let cancelled = false
    setLoading(true)
    setData(null)
    fetchFinalAssessmentDrawerData({ projectId, projectName })
      .then(payload => {
        if (cancelled) return
        setData(payload)
        if (initialStudentId && payload.students.some(s => s.id === initialStudentId)) {
          setSelectedStudentIds([initialStudentId])
        } else {
          setSelectedStudentIds([ALL_STUDENTS_VALUE])
        }
      })
      .catch(() => {
        if (cancelled) return
        notifications.show({
          message: tRef.current('arc_teacher_assessment_drawer.error.load_failed'),
          color: 'red',
          icon: <IconX size={18} />,
        })
        setData(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [opened, projectId, projectName, initialStudentId])

  // Reset on close.
  useEffect(() => {
    if (opened) return
    setData(null)
    setLoading(false)
    setOverwritePrompt(null)
    setSelectedStudentIds([ALL_STUDENTS_VALUE])
  }, [opened])

  // Student selection — exclusive between "All students" and individuals.
  const setStudentSelection = useCallback((next: string[]) => {
    if (next.length === 0) {
      setSelectedStudentIds([ALL_STUDENTS_VALUE])
      return
    }
    setSelectedStudentIds(prev => {
      const includesAll = next.includes(ALL_STUDENTS_VALUE)
      if (!includesAll) return next
      const hadAll = prev.includes(ALL_STUDENTS_VALUE)
      if (hadAll) {
        const individuals = next.filter(v => v !== ALL_STUDENTS_VALUE)
        return individuals.length > 0 ? individuals : [ALL_STUDENTS_VALUE]
      }
      return [ALL_STUDENTS_VALUE]
    })
  }, [])

  const visibleStudents = useMemo<FinalDrawerStudent[]>(() => {
    if (!data) return []
    if (selectedStudentIds.includes(ALL_STUDENTS_VALUE) || selectedStudentIds.length === 0) {
      return data.students
    }
    const set = new Set(selectedStudentIds)
    return data.students.filter(s => set.has(s.id))
  }, [data, selectedStudentIds])

  const studentOptions = useMemo(() => {
    const all = [{ value: ALL_STUDENTS_VALUE, label: tRef.current('arc_teacher_assessment_drawer.all_students') }]
    if (!data) return all
    return [...all, ...data.students.map(s => ({ value: s.id, label: s.fullName }))]
  }, [data])

  /** Immutably patches one per-student master score in `data`. */
  const updateLocalScore = useCallback((itemKey: string, studentId: string, masterScore: number) => {
    setData(prev => {
      if (!prev) return prev
      const apply = (items: FinalAssessmentItem[]): FinalAssessmentItem[] =>
        items.map(item => {
          if (item.lv4ItemId !== itemKey) return item
          const ps = item.perStudent[studentId]
          if (!ps) return item
          return {
            ...item,
            perStudent: { ...item.perStudent, [studentId]: { ...ps, masterScore } },
          }
        })
      return { ...prev, standards: apply(prev.standards), skills: apply(prev.skills) }
    })
  }, [])

  const findItem = useCallback(
    (itemKey: string): FinalAssessmentItem | undefined => {
      if (!data) return undefined
      return [...data.standards, ...data.skills].find(i => i.lv4ItemId === itemKey)
    },
    [data],
  )

  // ─── Per-student score (auto-save on change) ───────────────────────────────
  const setStudentScore = useCallback(
    async (itemKey: string, studentId: string, masterScore: number) => {
      const item = findItem(itemKey)
      const current = item?.perStudent[studentId]
      if (!current) return
      if (current.masterScore === masterScore && !current.doNotAssess) return
      const previous = current.masterScore
      updateLocalScore(itemKey, studentId, masterScore)
      try {
        await updater.updateIndividual(current.id, { masterScore })
        notifications.show({ message: tRef.current('arc_teacher_assessment_drawer.toast.saved'), color: 'green' })
      } catch {
        updateLocalScore(itemKey, studentId, previous)
        notifications.show({
          message: tRef.current('arc_teacher_assessment_drawer.error.save_failed'),
          color: 'red',
          icon: <IconX size={18} />,
        })
      }
    },
    [findItem, updateLocalScore, updater],
  )

  // ─── Bulk apply by lv4 item ────────────────────────────────────────────────
  const performBulk = useCallback(
    async (itemKey: string, masterScore: number) => {
      const item = findItem(itemKey)
      if (!item) return
      const targetIds = visibleStudents
        .map(s => s.id)
        .filter(sid => {
          const ps = item.perStudent[sid]
          return ps !== undefined && !ps.doNotAssess
        })
      if (targetIds.length === 0) return
      if (targetIds.every(sid => item.perStudent[sid].masterScore === masterScore)) return

      const snapshot = new Map(targetIds.map(sid => [sid, item.perStudent[sid].masterScore]))
      for (const sid of targetIds) updateLocalScore(itemKey, sid, masterScore)

      try {
        await updater.updateBulk(item.lv4ItemId, targetIds, { masterScore })
        notifications.show({ message: tRef.current('arc_teacher_assessment_drawer.toast.saved'), color: 'green' })
      } catch {
        for (const [sid, prev] of snapshot.entries()) updateLocalScore(itemKey, sid, prev)
        notifications.show({
          message: tRef.current('arc_teacher_assessment_drawer.error.save_failed'),
          color: 'red',
          icon: <IconX size={18} />,
        })
      }
    },
    [findItem, updateLocalScore, updater, visibleStudents],
  )

  const applyBulkScore = useCallback(
    async (itemKey: string, masterScore: number) => {
      const item = findItem(itemKey)
      if (!item) return
      const affected = visibleStudents
        .map(s => s.id)
        .filter(sid => {
          const ps = item.perStudent[sid]
          if (!ps || ps.doNotAssess) return false
          return ps.masterScore !== 0 && ps.masterScore !== masterScore
        })
      if (affected.length > 0) {
        setOverwritePrompt({ itemKey, masterScore, affectedStudentIds: affected })
        return
      }
      await performBulk(itemKey, masterScore)
    },
    [findItem, performBulk, visibleStudents],
  )

  const dismissOverwritePrompt = useCallback(() => setOverwritePrompt(null), [])

  const confirmOverwritePrompt = useCallback(() => {
    const prompt = overwritePrompt
    if (!prompt) return
    setOverwritePrompt(null)
    void performBulk(prompt.itemKey, prompt.masterScore)
  }, [overwritePrompt, performBulk])

  return {
    loading,
    data,
    selectedStudentIds,
    visibleStudents,
    studentOptions,
    setStudentSelection,
    overwritePrompt,
    dismissOverwritePrompt,
    confirmOverwritePrompt,
    setStudentScore,
    applyBulkScore,
  }
}
